import { ChangeDetectionStrategy, Component, HostBinding, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import Fuse from 'fuse.js';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DriversService } from '../../../root/drivers.service';
import { LoHiLoadsListItem } from '../../../types';
import { sortBy } from '../../../utilities/commonSorters';
import { fuse } from '../../../utilities/fuse';
import { loadSorting, saveSorting } from '../../../utilities/sortingSave';

const searchOptions: Fuse.IFuseOptions<LoHiLoadsListItem> = {
  distance: 100,
  keys: ['id', 'name', 'uniqueId', 'phone'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

type LoadSorter = (a: LoHiLoadsListItem, b: LoHiLoadsListItem) => number;

@Component({
  selector: 'lohi-completed-loads',
  templateUrl: './completed-loads-for-driver.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompletedLoadsForDriverComponent implements OnInit {
  @HostBinding('class.overflow-hidden') public noScroll = true;
  public currentDriverCompletedLoads$: Observable<LoHiLoadsListItem[]>;
  public searchForm = new FormControl(null);

  public sortBy: keyof LoHiLoadsListItem = 'completedTimestamp';
  public sortReverse = true;
  public sortingFunction$$ = new BehaviorSubject<LoadSorter>(getSortBy(this.sortBy, this.sortReverse));

  constructor(private driversService: DriversService) {}

  public ngOnInit(): void {
    const sortingData = loadSorting('completed-loads-driver');
    if (sortingData) {
      this.sortBy = sortingData.sortBy;
      this.sortReverse = sortingData.sortDirection;
      this.sortingFunction$$.next(getSortBy(this.sortBy, this.sortReverse));
    }
    this.currentDriverCompletedLoads$ = combineLatest([
      this.driversService.currentDriverCompletedLoads$.pipe(map(fuse(searchOptions))),
      this.searchForm.valueChanges.pipe(startWith('')),
      this.sortingFunction$$.asObservable(),
    ]).pipe(
      map(([fused, searchTerm, sortFunc]) => {
        if (!searchTerm) {
          return sort(fused.data, sortFunc);
        }

        return sort(
          fused.fuse.search(searchTerm).map((r) => r.item),
          sortFunc,
        );
      }),
    );
  }

  public setSortHeader(key: keyof LoHiLoadsListItem) {
    if (key === this.sortBy) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortBy = key;
      this.sortReverse = false;
    }
    saveSorting('completed-loads-driver', this.sortBy, this.sortReverse);
    this.sortingFunction$$.next(getSortBy(this.sortBy, this.sortReverse));
  }
}

function getSortBy(key: keyof LoHiLoadsListItem, reverse: boolean): LoadSorter {
  const temp = sortBy(`${reverse ? '-' : ''}${key}`);
  return (a: LoHiLoadsListItem, b: LoHiLoadsListItem) => {
    return temp(a, b);
  };
}
